import toast from "react-hot-toast";
import {
    ADMIN_ORDER_CHARGES_UPDATE,
    ADMIN_ORDER_CHARGES_UPDATE_FAIL,
    ADMIN_ORDER_CHARGES_UPDATE_SUCCESS,
    ADMIN_ORDER_FINAL_UPDATE,
    ADMIN_ORDER_FINAL_UPDATE_FAIL,
    ADMIN_ORDER_FINAL_UPDATE_SUCCESS,
    ADMIN_ORDER_PACKAGE_NOTES_UPDATE,
    ADMIN_ORDER_PACKAGE_NOTES_UPDATE_FAIL,
    ADMIN_ORDER_PACKAGE_NOTES_UPDATE_SUCCESS,
    ADMIN_ORDER_DETAIL,
    ADMIN_ORDER_DETAIL_FAIL,
    ADMIN_ORDER_DETAIL_SUCCESS,
    ORDER_LIST,
    ORDER_LIST_FAIL,
    ORDER_LIST_SUCCESS,
    ORDER_STATUS_CHANGE,
    ORDER_STATUS_CHANGE_FAIL,
    ORDER_STATUS_CHANGE_SUCCESS,
    GROUP_CART_MONITORING_LIST,
    GROUP_CART_MONITORING_LIST_SUCCESS,
    GROUP_CART_MONITORING_LIST_FAIL,
} from "../../actions/admin/customer-actions";
import { ADMIN_DASHBOARD_COUNT, ADMIN_DASHBOARD_COUNT_FAIL, ADMIN_DASHBOARD_COUNT_SUCCESS } from "../../actions/admin/AdminActions";

function parseAdminOrderDetailPayload(payload) {
    if (!payload) return null;
    const candidate = payload.data ?? payload;
    if (
        candidate?.id != null &&
        typeof candidate === "object" &&
        !Array.isArray(candidate)
    ) {
        return candidate;
    }
    if (candidate?.data?.id != null) return candidate.data;
    return null;
}

const initialState = {
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: "",
    totalPages: 0,
    total: 0,
    recordList: [],
    orderDetail: null,
    orderDetailLoading: false,
    orderDetailError: null,
    orderChargesSaving: false,
    orderFinalUpdateLoading: false,
    orderPackageNotesSaving: false,
    groupCartList: [],
    groupCartTotal: 0,
    /** Group cart monitoring table (admin) — separate from `isLoading` so order list / dashboard are not affected. */
    isGroupCartMonitoringLoading: false,
    hasFetchedGroupCartMonitoringList: false,
    dashboardCount: null,
};

const orderDataReducer = (state = initialState, { type, payload }) => {
    switch (type) {

        case ORDER_LIST: {
            return {
                ...state,
                isLoading: true,
            };
        }

        case ORDER_LIST_SUCCESS: {
            return {
                ...state,
                recordList: payload.data.data,
                total: payload.data.total,
                totalPages: payload.data.totalPages,
                isLoading: false,
            };
        }

        case ORDER_LIST_FAIL: {
            return {
                ...state,
                isLoading: false,
            };
        }

        case ADMIN_ORDER_DETAIL: {
            return {
                ...state,
                orderDetailLoading: true,
                orderDetailError: null,
                orderDetail: null,
            };
        }
        case ADMIN_ORDER_DETAIL_SUCCESS: {
            return {
                ...state,
                orderDetail: parseAdminOrderDetailPayload(payload),
                orderDetailLoading: false,
                orderDetailError: null,
            };
        }
        case ADMIN_ORDER_DETAIL_FAIL: {
            return {
                ...state,
                orderDetailLoading: false,
                orderDetailError: payload?.error?.data?.message || "Error",
                orderDetail: null,
            };
        }

        case ADMIN_ORDER_CHARGES_UPDATE: {
            return { ...state, orderChargesSaving: true };
        }
        case ADMIN_ORDER_CHARGES_UPDATE_SUCCESS: {
            const orderId = payload.orderId;
            const updated = payload.order;
            const charges = payload.charges || {};
            const mergeCharges = (order) => {
                if (!order || orderId == null) return order;
                if (String(order.id) !== String(orderId)) return order;
                if (updated && typeof updated === "object") {
                    return { ...order, ...updated };
                }
                return {
                    ...order,
                    ...charges,
                    total_shipping_weight: charges.total_shipping_weight,
                    shipping_cost_per_kg: charges.shipping_cost_per_kg,
                    delivery_charge: charges.delivery_charge,
                    marketplace_fee: charges.marketplace_fee,
                    tax: charges.tax,
                    extra_charges: charges.extra_charges,
                    grand_total: charges.grand_total,
                    total_amount: charges.total_amount,
                };
            };
            toast.success(payload.message || "Order charges saved");
            return {
                ...state,
                orderChargesSaving: false,
                recordList: state.recordList.map(mergeCharges),
                orderDetail: mergeCharges(state.orderDetail),
            };
        }
        case ADMIN_ORDER_CHARGES_UPDATE_FAIL: {
            toast.error(
                payload?.error?.data?.message ||
                    "Could not save charges. Backend endpoint may not be ready yet."
            );
            return { ...state, orderChargesSaving: false };
        }

        case ADMIN_ORDER_FINAL_UPDATE: {
            return { ...state, orderFinalUpdateLoading: true };
        }
        case ADMIN_ORDER_FINAL_UPDATE_SUCCESS: {
            const orderId = payload.orderId;
            const updated = payload.order;
            const mergeFinal = (order) => {
                if (!order || orderId == null) return order;
                if (String(order.id) !== String(orderId)) return order;
                if (updated && typeof updated === "object") {
                    return { ...order, ...updated };
                }
                return {
                    ...order,
                    is_finalized: true,
                    order_status: 'bill_generated',
                    final_update_sent_at: new Date().toISOString(),
                };
            };
            toast.success(payload.message || "Final update sent to customer");
            return {
                ...state,
                orderFinalUpdateLoading: false,
                recordList: state.recordList.map(mergeFinal),
                orderDetail: mergeFinal(state.orderDetail),
            };
        }
        case ADMIN_ORDER_FINAL_UPDATE_FAIL: {
            toast.error(
                payload?.error?.data?.message ||
                    "Could not send final update. Check backend endpoint."
            );
            return { ...state, orderFinalUpdateLoading: false };
        }

        case ADMIN_ORDER_PACKAGE_NOTES_UPDATE: {
            return { ...state, orderPackageNotesSaving: true };
        }
        case ADMIN_ORDER_PACKAGE_NOTES_UPDATE_SUCCESS: {
            const orderId = payload.orderId;
            const updated = payload.order;
            const notes = payload.package_notes;
            const mergeNotes = (order) => {
                if (!order || orderId == null) return order;
                if (String(order.id) !== String(orderId)) return order;
                if (updated && typeof updated === "object") {
                    return { ...order, ...updated };
                }
                return {
                    ...order,
                    package_notes: notes ?? "",
                };
            };
            toast.success(payload.message || "Package notes saved");
            return {
                ...state,
                orderPackageNotesSaving: false,
                recordList: state.recordList.map(mergeNotes),
                orderDetail: mergeNotes(state.orderDetail),
            };
        }
        case ADMIN_ORDER_PACKAGE_NOTES_UPDATE_FAIL: {
            toast.error(
                payload?.error?.data?.message || "Could not save package notes."
            );
            return { ...state, orderPackageNotesSaving: false };
        }

        case ORDER_STATUS_CHANGE: {
            return {
                ...state,
                isLoading: true,
            };
        }

        case ORDER_STATUS_CHANGE_SUCCESS: {
            toast.success(payload.message);
            const orderId = payload.orderId;
            const newStatus = payload.orderStatus;
            const newPaymentStatus = payload.paymentStatus;
            const syncStatus = (order) => {
                if (!order || orderId == null) return order;
                if (String(order.id) !== String(orderId)) return order;
                return {
                    ...order,
                    ...(newStatus != null && newStatus !== ""
                        ? { order_status: newStatus }
                        : {}),
                    ...(newPaymentStatus != null && newPaymentStatus !== ""
                        ? { payment_status: newPaymentStatus }
                        : {}),
                };
            };
            return {
                ...state,
                isLoading: false,
                recordList: state.recordList.map(syncStatus),
                orderDetail: syncStatus(state.orderDetail),
            };
        }

        case ORDER_STATUS_CHANGE_FAIL: {
            toast.error(payload.error?.data?.message);
            return {
                ...state,
                isLoading: false,
            };
        }

        case ADMIN_DASHBOARD_COUNT: {
            return { ...state, isLoading: true };
        }
        case ADMIN_DASHBOARD_COUNT_SUCCESS: {
            return {
                ...state,
                isLoading: false,
                dashboardCount: payload.data,
            };
        }
        case ADMIN_DASHBOARD_COUNT_FAIL: {
            return { ...state, isLoading: false };
        }

        case GROUP_CART_MONITORING_LIST: {
            return {
                ...state,
                isGroupCartMonitoringLoading: true,
            };
        }
        case GROUP_CART_MONITORING_LIST_SUCCESS: {
            return {
                ...state,
                isGroupCartMonitoringLoading: false,
                hasFetchedGroupCartMonitoringList: true,
                groupCartList: payload.data.data,
                groupCartTotal: payload.data.total,
            };
        }
        case GROUP_CART_MONITORING_LIST_FAIL: {
            return {
                ...state,
                isGroupCartMonitoringLoading: false,
                hasFetchedGroupCartMonitoringList: true,
            };
        }

        default: {
            return { ...state };
        }
    }
};

export default orderDataReducer;
